/**
 * InputsDesigner.tsx — add / edit / reorder / remove a calculator's input fields.
 * Fields: key, label, type, unit, options (for select), required.
 */

import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import type { StudioInputField } from '../../../api/calculatorBuilder';
import { StudioPanel, field, btn, btnAccent, label, btnGhostDanger } from './ui';

interface Props {
  inputs: StudioInputField[];
  onChange: (next: StudioInputField[]) => void;
}

const TYPES: StudioInputField['type'][] = ['number', 'select', 'toggle', 'text'];

export default function InputsDesigner({ inputs, onChange }: Props) {
  const patch = (i: number, p: Partial<StudioInputField>) => {
    const next = inputs.map((f, idx) => (idx === i ? { ...f, ...p } : f));
    onChange(next);
  };
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= inputs.length) return;
    const next = [...inputs];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const add = () =>
    onChange([...inputs, { key: `field_${inputs.length + 1}`, label: 'New field', type: 'number', required: false }]);
  const remove = (i: number) => onChange(inputs.filter((_, idx) => idx !== i));

  return (
    <StudioPanel
      title="Inputs"
      subtitle="The form the rep fills in. Add variables here — they appear in the formula + HTML chip palettes automatically."
      right={
        <button type="button" style={btnAccent} onClick={add}>
          <Plus size={14} /> Add field
        </button>
      }
    >
      {inputs.length === 0 && (
        <div style={{ fontSize: 13, color: 'var(--ink-3, #6b7280)', padding: '8px 0' }}>No input fields yet.</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {inputs.map((f, i) => (
          <div
            key={i}
            style={{
              border: '1px solid var(--line, #e5e7eb)',
              borderRadius: 9,
              padding: 12,
              background: 'var(--bg-0, #fff)',
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px 90px', gap: 10 }}>
              <div>
                <span style={label}>Key</span>
                <input
                  style={{ ...field, width: '100%', fontFamily: 'ui-monospace, monospace' }}
                  value={f.key}
                  onChange={(e) => patch(i, { key: e.target.value.trim() })}
                />
              </div>
              <div>
                <span style={label}>Label</span>
                <input style={{ ...field, width: '100%' }} value={f.label} onChange={(e) => patch(i, { label: e.target.value })} />
              </div>
              <div>
                <span style={label}>Type</span>
                <select style={{ ...field, width: '100%' }} value={f.type} onChange={(e) => patch(i, { type: e.target.value as StudioInputField['type'] })}>
                  {TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <span style={label}>Unit</span>
                <input style={{ ...field, width: '100%' }} value={f.unit ?? ''} onChange={(e) => patch(i, { unit: e.target.value || undefined })} />
              </div>
            </div>

            {f.type === 'select' && (
              <div style={{ marginTop: 10 }}>
                <span style={label}>Options (value:label per line, or plain value)</span>
                <textarea
                  style={{ ...field, width: '100%', height: 66, padding: 8, resize: 'vertical', fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                  value={(f.options ?? []).map((o) => (o.label && o.label !== String(o.value) ? `${o.value}:${o.label}` : String(o.value))).join('\n')}
                  onChange={(e) =>
                    patch(i, {
                      options: e.target.value
                        .split('\n')
                        .map((line) => line.trim())
                        .filter(Boolean)
                        .map((line) => {
                          const [value, ...rest] = line.split(':');
                          const lbl = rest.join(':').trim();
                          return { value: value.trim(), label: lbl || value.trim() };
                        }),
                    })
                  }
                />
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--ink-2, #374151)' }}>
                <input type="checkbox" checked={!!f.required} onChange={(e) => patch(i, { required: e.target.checked })} />
                Required
              </label>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button type="button" style={{ ...btn, height: 28, padding: '0 8px' }} onClick={() => move(i, -1)} disabled={i === 0}>
                  <ArrowUp size={14} />
                </button>
                <button type="button" style={{ ...btn, height: 28, padding: '0 8px' }} onClick={() => move(i, 1)} disabled={i === inputs.length - 1}>
                  <ArrowDown size={14} />
                </button>
                <button type="button" style={btnGhostDanger} onClick={() => remove(i)}>
                  <Trash2 size={14} /> Remove
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </StudioPanel>
  );
}
